import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  TradingViewChart,
  SignalList,
  TimeframeSelector,
  ConnectionStatus,
  LoginForm,
  DomModeSelector,
} from '@/components'
import { useAuth } from '@/contexts/AuthContext'
import { useWebSocket } from '@/hooks/useWebSocket'
import { useBinanceKline } from '@/hooks/useBinanceKline'
import { fetchCandles, fetchCandlesRange } from '@/services/binance'
import { getActiveSignals, getSignals, getMonitoringSettings } from '@/services/api'
import type { Timeframe, Signal, Candle } from '@/types'

const DEFAULT_SYMBOL = 'BTCUSDT'

// Timeframe durations in milliseconds
const TIMEFRAME_MS: Record<Timeframe, number> = {
  '1m': 60 * 1000,
  '5m': 5 * 60 * 1000,
  '15m': 15 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '4h': 4 * 60 * 60 * 1000,
  '1d': 24 * 60 * 60 * 1000,
}

// Debounce helper
function useDebouncedCallback<T extends (...args: unknown[]) => void>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  return useCallback(
    ((...args: unknown[]) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args)
      }, delay)
    }) as T,
    [delay]
  )
}

function App() {
  const { isAuthenticated, isLoading: authLoading, authEnabled, telegramUsername, logout } = useAuth()
  const [symbol] = useState(DEFAULT_SYMBOL)
  const [timeframe, setTimeframe] = useState<Timeframe>('5m')
  const [signals, setSignals] = useState<Signal[]>([])
  const [candles, setCandles] = useState<Candle[]>([])
  const [visibleTimeRange, setVisibleTimeRange] = useState<{ from: number; to: number } | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const oldestLoadedTime = useRef<number | null>(null)
  const loadedSignalRanges = useRef<Set<string>>(new Set())
  const isLoadingMoreRef = useRef(false)
  // Bumped on every timeframe/symbol switch so an in-flight loadOlderCandles
  // request from the previous timeframe can detect it's stale and bail out
  // instead of merging mismatched-resolution candles into the new state.
  const dataGenerationRef = useRef(0)

  const shouldShowApp = !authLoading && (!authEnabled || isAuthenticated)

  // Fetch initial candle data
  const {
    data: initialCandles = [],
    isLoading: candlesLoading,
  } = useQuery({
    queryKey: ['candles', symbol, timeframe],
    queryFn: () => fetchCandles(symbol, timeframe, 500),
    enabled: shouldShowApp,
  })

  // Set initial candles and load signals for them
  useEffect(() => {
    if (initialCandles.length > 0) {
      setCandles(initialCandles)
      oldestLoadedTime.current = initialCandles[0]?.time || null
      loadedSignalRanges.current.clear()

      // Load signals for the initial candle range
      const fromTime = initialCandles[0].time
      const toTime = initialCandles[initialCandles.length - 1].time
      loadSignalsForRange(fromTime, toTime)
    }
  }, [initialCandles])

  // Fetch initial active signals
  const { data: fetchedSignals, isLoading: signalsLoading } = useQuery({
    queryKey: ['activeSignals', symbol],
    queryFn: () => getActiveSignals(symbol),
    refetchInterval: 30000,
    enabled: shouldShowApp,
  })

  // Update with active signals
  useEffect(() => {
    if (fetchedSignals) {
      setSignals(prev => {
        const existingIds = new Set(prev.map(s => s.id))
        const newSignals = fetchedSignals.filter(s => !existingIds.has(s.id))
        if (newSignals.length === 0) return prev
        return [...newSignals, ...prev].sort(
          (a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime()
        )
      })
    }
  }, [fetchedSignals])

  // Load signals for a time range
  const loadSignalsForRange = useCallback(async (fromTime: number, toTime: number) => {
    // Round to reasonable chunks to avoid too many small requests
    const chunkSize = TIMEFRAME_MS[timeframe] * 100 / 1000 // 100 candles worth
    const roundedFrom = Math.floor(fromTime / chunkSize) * chunkSize
    const roundedTo = Math.ceil(toTime / chunkSize) * chunkSize

    const rangeKey = `${roundedFrom}-${roundedTo}`
    if (loadedSignalRanges.current.has(rangeKey)) return

    try {
      const response = await getSignals({
        symbol,
        from_time: new Date(roundedFrom * 1000).toISOString(),
        to_time: new Date(roundedTo * 1000).toISOString(),
        page_size: 1000,
      })

      loadedSignalRanges.current.add(rangeKey)

      if (response.items.length > 0) {
        setSignals(prev => {
          const existingIds = new Set(prev.map(s => s.id))
          const newSignals = response.items.filter(s => !existingIds.has(s.id))
          if (newSignals.length === 0) return prev
          return [...prev, ...newSignals].sort(
            (a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime()
          )
        })
      }
    } catch (error) {
      console.error('Failed to load signals:', error)
    }
  }, [symbol, timeframe])

  // Load older candles
  const loadOlderCandles = useCallback(async () => {
    if (isLoadingMoreRef.current || !oldestLoadedTime.current) return

    const generation = dataGenerationRef.current
    isLoadingMoreRef.current = true
    setIsLoadingMore(true)

    try {
      const endTime = oldestLoadedTime.current * 1000 - 1
      const candleMs = TIMEFRAME_MS[timeframe]
      const startTime = endTime - (500 * candleMs)

      const olderCandles = await fetchCandlesRange(symbol, timeframe, startTime, endTime)

      // The timeframe/symbol changed while this request was in flight --
      // these candles are for a resolution/state that no longer applies.
      if (generation !== dataGenerationRef.current) return

      if (olderCandles.length > 0) {
        setCandles(prev => {
          const timeSet = new Set(prev.map(c => c.time))
          const newCandles = olderCandles.filter(c => !timeSet.has(c.time))
          if (newCandles.length === 0) return prev
          const merged = [...newCandles, ...prev].sort((a, b) => a.time - b.time)
          oldestLoadedTime.current = merged[0]?.time || oldestLoadedTime.current
          return merged
        })

        // Load signals for the new range
        const fromTime = olderCandles[0].time
        const toTime = olderCandles[olderCandles.length - 1].time
        await loadSignalsForRange(fromTime, toTime)
      }
    } catch (error) {
      console.error('Failed to load older candles:', error)
    } finally {
      if (generation === dataGenerationRef.current) {
        isLoadingMoreRef.current = false
        setIsLoadingMore(false)
      }
    }
  }, [timeframe, symbol, loadSignalsForRange])

  // Debounced version of loadOlderCandles
  const debouncedLoadOlderCandles = useDebouncedCallback(loadOlderCandles, 300)

  // Handle visible range change from chart
  const handleVisibleRangeChange = useCallback((fromIndex: number, toIndex: number) => {
    if (candles.length === 0) return

    // Clamp indices to valid range
    const clampedFrom = Math.max(0, Math.min(fromIndex, candles.length - 1))
    const clampedTo = Math.max(0, Math.min(toIndex, candles.length - 1))

    const fromCandle = candles[clampedFrom]
    const toCandle = candles[clampedTo]

    if (fromCandle && toCandle) {
      setVisibleTimeRange({ from: fromCandle.time, to: toCandle.time })

      // Load more candles if we're near the beginning (with debounce)
      if (fromIndex < 100 && !isLoadingMoreRef.current) {
        debouncedLoadOlderCandles()
      }
    }
  }, [candles, debouncedLoadOlderCandles])

  // Current DOM display mode ("precise" | "aggregated" | "both"). The bot
  // always detects and stores both types; this only filters what's shown.
  // Shares the ['monitoringSettings'] query cache with <DomModeSelector />.
  const { data: monitoringSettings } = useQuery({
    queryKey: ['monitoringSettings'],
    queryFn: getMonitoringSettings,
    staleTime: 10000,
    refetchInterval: 15000,
  })
  const domMode = monitoringSettings?.dom_mode ?? 'both'

  // A signal passes the DOM-mode filter when: "both" is selected, it's not a
  // DOM signal (trades always show), it's an untagged legacy signal (can't be
  // classified — kept visible), or its tag matches the selected mode.
  const matchesDomMode = useCallback((s: Signal) => {
    if (domMode === 'both') return true
    if (s.source !== 'dom') return true
    if (!s.dom_mode) return true
    return s.dom_mode === domMode
  }, [domMode])

  // Filter signals to show only those in visible range (and matching the mode)
  const visibleSignals = useMemo(() => {
    const byMode = signals.filter(matchesDomMode)

    if (!visibleTimeRange || candles.length === 0) {
      // Show all mode-matching signals if no visible range yet
      return byMode
    }

    // Add padding (10 candles worth on each side)
    const padding = TIMEFRAME_MS[timeframe] / 1000 * 10
    const from = visibleTimeRange.from - padding
    const to = visibleTimeRange.to + padding

    return byMode.filter(s => {
      const signalTime = new Date(s.detected_at).getTime() / 1000
      return signalTime >= from && signalTime <= to
    })
  }, [signals, visibleTimeRange, timeframe, candles.length, matchesDomMode])

  // Handle new signal from WebSocket
  const handleSignalNew = useCallback((signal: Signal) => {
    setSignals((prev) => {
      const filtered = prev.filter((s) => s.id !== signal.id)
      return [signal, ...filtered]
    })
  }, [])

  // Handle signal disappeared from WebSocket
  const handleSignalDisappeared = useCallback((signalId: string) => {
    setSignals((prev) =>
      prev.map((s) =>
        s.id === signalId
          ? { ...s, status: 'disappeared' as const }
          : s
      )
    )
  }, [])

  // Binance kline WebSocket for real-time chart updates
  const realtimeCandle = useBinanceKline({
    symbol,
    timeframe,
    enabled: shouldShowApp,
  })

  // WebSocket connection
  const { isConnected } = useWebSocket({
    symbol,
    onSignalNew: handleSignalNew,
    onSignalDisappeared: handleSignalDisappeared,
  })

  // Handle timeframe change
  const handleTimeframeChange = useCallback((newTimeframe: Timeframe) => {
    dataGenerationRef.current += 1
    isLoadingMoreRef.current = false
    setIsLoadingMore(false)
    setTimeframe(newTimeframe)
    setCandles([])
    setSignals([])
    oldestLoadedTime.current = null
    loadedSignalRanges.current.clear()
    setVisibleTimeRange(null)
  }, [])

  // Show loading spinner while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-chart-bg">
        <div className="text-text-secondary">Loading...</div>
      </div>
    )
  }

  // Show login form if auth is enabled and not authenticated
  if (authEnabled && !isAuthenticated) {
    return <LoginForm />
  }

  return (
    <div className="flex flex-col h-screen bg-chart-bg">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-chart-grid">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-text-primary">
            Hammer Alert System
          </h1>
          <div className="text-lg font-mono text-text-secondary">{symbol}</div>
          {isLoadingMore && (
            <div className="text-xs text-text-secondary animate-pulse">
              Loading history...
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          <DomModeSelector />
          <TimeframeSelector
            value={timeframe}
            onChange={handleTimeframeChange}
          />
          <ConnectionStatus isConnected={isConnected} />
          {authEnabled && isAuthenticated && (
            <div className="flex items-center gap-3 pl-3 border-l border-chart-grid">
              {telegramUsername && (
                <span className="text-xs text-text-secondary">
                  @{telegramUsername}
                </span>
              )}
              <button
                onClick={logout}
                className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary
                           bg-chart-bg border border-chart-grid rounded
                           hover:border-signal-ask/50 transition-colors"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Chart area */}
        <div className="flex-1 relative">
          {candlesLoading ? (
            <div className="flex items-center justify-center h-full text-text-secondary">
              Loading chart data...
            </div>
          ) : (
            <TradingViewChart
              candles={candles}
              signals={visibleSignals}
              symbol={symbol}
              timeframe={timeframe}
              realtimeCandle={realtimeCandle}
              onVisibleRangeChange={handleVisibleRangeChange}
            />
          )}
        </div>

        {/* Signal sidebar */}
        <aside className="w-80 border-l border-chart-grid bg-chart-bg">
          <SignalList
            signals={visibleSignals}
            isLoading={signalsLoading}
            title={`Signals (${visibleSignals.length})`}
          />
        </aside>
      </div>

      {/* Footer */}
      <footer className="px-4 py-2 border-t border-chart-grid text-xs text-text-secondary text-center">
        Hammer Alert System v2.0 | {signals.length} signals loaded | {candles.length} candles
        {visibleTimeRange && ` | ${new Date(visibleTimeRange.from * 1000).toLocaleDateString()} - ${new Date(visibleTimeRange.to * 1000).toLocaleDateString()}`}
      </footer>
    </div>
  )
}

export default App
